import { effectiveDimensions } from "../effective";
import {
  effectiveZoneDistanceM,
  zoneIdByNode,
  zonePairKey,
  zonesOf,
} from "../zone-lengths";
import type {
  ComponentDefinition,
  ComponentNode,
  System,
  ZoneNode,
} from "../types";

/**
 * A `ZoneNode` is a React Flow parent — one rectangle, one owner per node — so a group can
 * only become a zone if nothing outside it sits in that rectangle. The negative bus files
 * with the **panel**, not the loads it is drawn beside, or the bank's return is measured
 * across the living area. `isEngineBay` is never set: a guess silently fattens wire.
 */
export type GeneratedZoneKind = "roof" | "bank" | "panel" | "loads";

/** Draw order, and the order the review lists pairs in. */
export const GENERATED_ZONE_ORDER: readonly GeneratedZoneKind[] = [
  "roof",
  "bank",
  "panel",
  "loads",
];

/** Untranslated: a zone label is graph data the user edits, not chrome. */
export const GENERATED_ZONE_LABEL: Record<GeneratedZoneKind, string> = {
  roof: "Roof",
  bank: "Battery bank",
  panel: "Electrical panel",
  loads: "Living area",
};

const ZONE_PADDING_PX = 36;

/** React Flow's resizer floor, honoured so a one-part zone is still grabbable. */
const MIN_ZONE_W = 200;
const MIN_ZONE_H = 140;

type Rect = { x: number; y: number; width: number; height: number };

/**
 * Mutates `nodes`: each member gains a `parentId` and has its position re-based into the
 * zone's coordinate space, which React Flow requires to be applied together. Returns `[]`
 * — leaving the system unzoned — rather than shipping overlapping rectangles.
 */
export function planGeneratedZones(
  nodes: ComponentNode[],
  defByNodeId: Map<string, ComponentDefinition>,
  groupByNode: Map<string, GeneratedZoneKind>,
  takenIds: Set<string> = new Set(),
): ZoneNode[] {
  const members = new Map<GeneratedZoneKind, ComponentNode[]>();
  for (const node of nodes) {
    const kind = groupByNode.get(node.id);
    if (!kind) continue;
    const list = members.get(kind);
    if (list) list.push(node);
    else members.set(kind, [node]);
  }
  if (members.size < 2) return [];

  const rects = new Map<GeneratedZoneKind, Rect>();
  for (const kind of GENERATED_ZONE_ORDER) {
    const group = members.get(kind);
    if (!group?.length) continue;
    rects.set(kind, paddedBounds(group, defByNodeId));
  }

  const list = [...rects.values()];
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      if (overlaps(list[i], list[j])) return [];
    }
  }

  const zones: ZoneNode[] = [];
  for (const kind of GENERATED_ZONE_ORDER) {
    const rect = rects.get(kind);
    if (!rect) continue;
    const id = uniqueZoneId(`zone-${kind}`, takenIds);
    zones.push({
      id,
      kind: "zone",
      position: { x: rect.x, y: rect.y },
      width: rect.width,
      height: rect.height,
      label: GENERATED_ZONE_LABEL[kind],
    });
    for (const node of members.get(kind)!) {
      node.parentId = id;
      node.position = {
        x: Math.round(node.position.x - rect.x),
        y: Math.round(node.position.y - rect.y),
      };
    }
  }
  return zones;
}

function paddedBounds(
  group: ComponentNode[],
  defByNodeId: Map<string, ComponentDefinition>,
): Rect {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const node of group) {
    const size = effectiveDimensions(defByNodeId.get(node.id)!, node);
    left = Math.min(left, node.position.x);
    top = Math.min(top, node.position.y);
    right = Math.max(right, node.position.x + size.width);
    bottom = Math.max(bottom, node.position.y + size.height);
  }
  return {
    x: Math.round(left - ZONE_PADDING_PX),
    y: Math.round(top - ZONE_PADDING_PX),
    width: Math.max(MIN_ZONE_W, Math.round(right - left + ZONE_PADDING_PX * 2)),
    height: Math.max(MIN_ZONE_H, Math.round(bottom - top + ZONE_PADDING_PX * 2)),
  };
}

function overlaps(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    b.x < a.x + a.width &&
    a.y < b.y + b.height &&
    b.y < a.y + a.height
  );
}

export type ZoneDistanceRow = {
  /** `zonePairKey(fromZoneId, toZoneId)`. */
  key: string;
  fromZoneId: string;
  toZoneId: string;
  fromLabel: string;
  toLabel: string;
  /** Metres: the stored answer, or what the runs imply. */
  distanceM: number;
  cableCount: number;
  /** Indexes into `AiSystemDraft.connections`. */
  connectionIndexes: number[];
};

export function zoneDistanceRows(
  system: System,
  cableIdsByConnection: readonly (readonly string[])[],
): ZoneDistanceRow[] {
  const zones = zonesOf(system);
  if (zones.length < 2) return [];
  const labelOf = new Map(zones.map((z) => [z.id, z.label ?? "Zone"]));
  const rank = new Map(zones.map((z, i) => [z.id, i]));
  const zoneByNode = zoneIdByNode(system);

  const connectionOfCable = new Map<string, number>();
  cableIdsByConnection.forEach((ids, index) => {
    for (const id of ids) connectionOfCable.set(id, index);
  });

  const rows = new Map<string, ZoneDistanceRow>();
  for (const cable of system.cables) {
    const a = zoneByNode.get(cable.fromNodeId);
    const b = zoneByNode.get(cable.toNodeId);
    if (!a || !b || a === b) continue;
    const key = zonePairKey(a, b);
    let row = rows.get(key);
    if (!row) {
      const [from, to] = (rank.get(a) ?? 0) <= (rank.get(b) ?? 0) ? [a, b] : [b, a];
      row = {
        key,
        fromZoneId: from,
        toZoneId: to,
        fromLabel: labelOf.get(from) ?? from,
        toLabel: labelOf.get(to) ?? to,
        distanceM: effectiveZoneDistanceM(system, from, to, zoneByNode) ?? 0,
        cableCount: 0,
        connectionIndexes: [],
      };
      rows.set(key, row);
    }
    row.cableCount++;
    const index = connectionOfCable.get(cable.id);
    if (index !== undefined && !row.connectionIndexes.includes(index)) {
      row.connectionIndexes.push(index);
    }
  }

  return [...rows.values()].sort(
    (x, y) =>
      (rank.get(x.fromZoneId) ?? 0) - (rank.get(y.fromZoneId) ?? 0) ||
      (rank.get(x.toZoneId) ?? 0) - (rank.get(y.toZoneId) ?? 0),
  );
}

/** A part could be called `zone-loads`; ids are one namespace on a `System`. */
function uniqueZoneId(base: string, taken: Set<string>): string {
  let id = base;
  let n = 2;
  while (taken.has(id)) id = `${base}-${n++}`;
  taken.add(id);
  return id;
}
